import { useEffect, useState } from "react";
import IGlobalConfig from "../../../../../../types/IGlobalConfig";
import { ICharityMmsSetup } from "../../../../../../utils/firebase/mmsSetup";
import {
  getMmsOnboardingStatus,
  MmsOnboardingStatusResponse,
} from "../../../../../../utils/mmsOnboardingApi";
import { formatMmsApiErrorMessage } from "../../../../../../utils/mmsApiErrorMessage";

interface UseMmsAttentionStatusProps {
  charityId?: string;
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  mmsSetup: ICharityMmsSetup | null;
  mmsSetupLoaded: boolean;
}

export interface MmsAttentionStatus {
  needsAttention: boolean;
  pipelineRunning: boolean;
  setupFailed: boolean;
  statusError: string | null;
}

function derivePipelineRunning(status: MmsOnboardingStatusResponse): boolean {
  if (status.setupFailed) {
    return false;
  }

  const steps = status.steps ?? [];
  const hasActiveSteps = steps.some(
    step =>
      step.status === "in_progress" ||
      step.status === "blocked"
  );

  return (
    status.pendingBrandQueued ||
    status.pendingCampaignQueued ||
    status.pendingMessagingQueued ||
    hasActiveSteps
  );
}

export function useMmsAttentionStatus({
  charityId,
  config,
  serviceAccessToken,
  mmsSetup,
  mmsSetupLoaded,
}: UseMmsAttentionStatusProps): MmsAttentionStatus {
  const [needsAttention, setNeedsAttention] = useState(false);
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [setupFailed, setSetupFailed] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const shouldFetch =
    mmsSetupLoaded &&
    mmsSetup?.status === "pending" &&
    Boolean(mmsSetup?.mmsSenderAreaCode) &&
    Boolean(charityId) &&
    Boolean(config?.adminServiceUrl) &&
    Boolean(serviceAccessToken);

  useEffect(() => {
    if (!shouldFetch) {
      setNeedsAttention(false);
      setPipelineRunning(false);
      setSetupFailed(false);
      setStatusError(null);
      return;
    }

    let cancelled = false;

    getMmsOnboardingStatus(config!.adminServiceUrl!, charityId!, serviceAccessToken!)
      .then(status => {
        if (!cancelled) {
          setNeedsAttention(Boolean(status.needsAttention));
          setPipelineRunning(derivePipelineRunning(status));
          setSetupFailed(Boolean(status.setupFailed));
          setStatusError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setNeedsAttention(true);
          setPipelineRunning(false);
          setSetupFailed(false);
          setStatusError(
            formatMmsApiErrorMessage(
              err,
              "Unable to load MMS setup status. Open MMS setup to retry."
            )
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    shouldFetch,
    charityId,
    config?.adminServiceUrl,
    serviceAccessToken,
    mmsSetup?.status,
    mmsSetup?.mmsSenderAreaCode,
  ]);

  return { needsAttention, pipelineRunning, setupFailed, statusError };
}
